import { getSeoSiteUrl } from '../utils/seoSiteUrl';
import { paths } from '../routes/paths';

/**
 * Centralized SEO configuration for baseline schemas.
 * Provides consistent organization, website, and route metadata across all public pages.
 */

export interface RouteMetadata {
  path: string;
  label: string;
  parent?: string;
}

/**
 * Organization configuration for JSON-LD schema.
 * Used by OrganizationSchema and BaselineSchemaProvider.
 */
export const organizationConfig = {
  name: 'AttendIn',
  description:
    'AttendIn is the leading event management and attendee networking platform for modern events.',
  url: getSeoSiteUrl(),
  logo: `${getSeoSiteUrl()}/logo-side.png`,
  sameAs: [
    'https://www.linkedin.com/company/attendin',
    'https://twitter.com/attendinapp',
    'https://www.instagram.com/attendinapp',
  ],
  contact: {
    email: 'hello@attendin.io',
    telephone: '+44 (0) 20 7946 0958',
  },
};

/**
 * Website configuration for JSON-LD schema.
 * Used by WebsiteSchema and BaselineSchemaProvider.
 */
export const websiteConfig = {
  name: 'AttendIn',
  description:
    'Discover and register for events near you. Connect with organisers and fellow attendees.',
  url: getSeoSiteUrl(),
  searchUrl: `${getSeoSiteUrl()}/events?search={search_term_string}`,
};

/**
 * Route metadata for breadcrumb generation.
 * Maps public routes to human-readable labels and parent routes.
 * Used for automatic breadcrumb schema generation.
 */
export const publicRouteMetadata: Record<string, RouteMetadata> = {
  '/': {
    path: '/',
    label: 'Home',
  },
  '/events': {
    path: paths.public.searchEvents,
    label: 'Events',
    parent: '/',
  },
  '/organisers': {
    path: paths.public.eventOrganizers,
    label: 'Event Organisers',
    parent: '/',
  },
  '/contact': {
    path: paths.public.contact,
    label: 'Contact',
    parent: '/',
  },
  '/pricing': {
    path: paths.public.betaProgram,
    label: 'Pricing',
    parent: '/',
  },
  '/privacy-policy': {
    path: paths.public.privacyPolicyPage,
    label: 'Privacy Policy',
    parent: '/',
  },
  '/terms-of-service': {
    path: paths.public.termsOfServicePage,
    label: 'Terms of Service',
    parent: '/',
  },
  '/signup-terms': {
    path: paths.public.signupTermsPage,
    label: 'Sign Up Terms',
    parent: '/',
  },
  '/cookie-policy': {
    path: paths.public.cookiePolicyPage,
    label: 'Cookie Policy',
    parent: '/',
  },
  '/account-deletion': {
    path: paths.public.accountDeletionPage,
    label: 'Account Deletion',
    parent: '/',
  },
  '/unsubscribe': {
    path: paths.public.unsubscribe,
    label: 'Unsubscribe',
    parent: '/',
  },
  '/profile': {
    path: paths.public.profile,
    label: 'Profile',
    parent: '/',
  },
  '/my-events': {
    path: paths.public.myEvents,
    label: 'My Events',
    parent: '/',
  },
  '/my-tickets': {
    path: paths.public.tickets,
    label: 'My Tickets',
    parent: '/',
  },
  '/partner-dashboard': {
    path: paths.public.partnerDashboard,
    label: 'Partner Dashboard',
    parent: '/',
  },
  '/community-feed': {
    path: paths.public.communityFeed,
    label: 'Community Feed',
    parent: '/',
  },
  '/pods': {
    path: paths.public.pods,
    label: 'Pods',
    parent: '/',
  },
};

/**
 * Dynamic route patterns for routes with parameters (e.g., /events/:id).
 * Breadcrumbs for these routes should include their parent + dynamic segment label.
 */
export const dynamicRoutePatterns = {
  eventDetails: {
    pattern: /^\/events\/[^/]+$/,
    parentPath: '/events',
    getLabel: () => 'Event Details',
  },
  eventRegister: {
    pattern: /^\/events\/[^/]+\/register$/,
    parentPath: '/events',
    getLabel: () => 'Register',
  },
  eventAddTickets: {
    pattern: /^\/events\/[^/]+\/add-tickets$/,
    parentPath: '/events',
    getLabel: () => 'Add Tickets',
  },
  eventPayment: {
    pattern: /^\/events\/[^/]+\/payment$/,
    parentPath: '/events',
    getLabel: () => 'Payment',
  },
  eventConfirmation: {
    pattern: /^\/events\/[^/]+\/confirmation$/,
    parentPath: '/events',
    getLabel: () => 'Confirmation',
  },
  organiserProfile: {
    pattern: /^\/organisers\/[^/]+$/,
    parentPath: '/organisers',
    getLabel: () => 'Organiser Profile',
  },
  communityDetails: {
    pattern: /^\/communities\/[^/]+$/,
    parentPath: '/community-feed',
    getLabel: () => 'Community',
  },
  communityJoin: {
    pattern: /^\/communities\/[^/]+\/join$/,
    parentPath: '/community-feed',
    getLabel: () => 'Join Community',
  },
  podDetails: {
    pattern: /^\/pods\/[^/]+$/,
    parentPath: '/pods',
    getLabel: () => 'Pod',
  },
};

/**
 * Public-only routes that should have baseline schemas.
 * Used to filter which routes receive breadcrumb and schema injection.
 */
export const publicOnlyRoutes = [
  paths.public.root,
  paths.public.searchEvents,
  paths.public.contact,
  paths.public.betaProgram,
  paths.public.privacyPolicyPage,
  paths.public.termsOfServicePage,
  paths.public.signupTermsPage,
  paths.public.cookiePolicyPage,
  paths.public.accountDeletionPage,
  paths.public.unsubscribe,
  paths.public.profile,
  paths.public.myEvents,
  paths.public.tickets,
  paths.public.partnerDashboard,
  paths.public.communityFeed,
  paths.public.pods,
  paths.public.events.details,
  paths.public.events.register,
  paths.public.events.addTickets,
  paths.public.events.payment,
  paths.public.events.confirmation,
  paths.public.eventOrganizerProfile,
  paths.public.communityDetails,
  paths.public.join,
  paths.public.podDetails,
];
